import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { USER_DETAILS_QUERY_KEY } from "../../../api-data/iam/user";
import { SETTINGS_QUERY_KEY } from "../../../api-data/app-content/settings";
import {
  NOTIFICATION_CHANNELS_QUERY_KEY,
  NOTIFICATION_PROVIDERS_QUERY_KEY,
  NOTIFICATION_TEMPLATES_QUERY_KEY,
} from "../../../api-data/notifications";
import { LANGUAGES, type LangCode } from "./languages";

// Query keys whose responses are NOT locale-sensitive — skip on language change.
// NOTE: HOME_SCREEN_IMAGES_BASE_QUERY_KEY was removed from this list — banners
// now have translatable fields (title, subtitle, ctaLabel) and must re-fetch on
// locale change so the correct translations are shown.
const NON_LOCALE_QUERY_KEYS = new Set([
  USER_DETAILS_QUERY_KEY,
  SETTINGS_QUERY_KEY,
  NOTIFICATION_CHANNELS_QUERY_KEY,
  NOTIFICATION_PROVIDERS_QUERY_KEY,
  NOTIFICATION_TEMPLATES_QUERY_KEY,
]);

interface LanguageDialogProps {
  /** Controlled mode — used by Header / HomeBannerCarousel. */
  open?: boolean;
  onClose?: () => void;
}

const LanguageDialog: React.FC<LanguageDialogProps> = ({ open: openProp, onClose }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const currentLang = (i18n.language || "en") as LangCode;

  // Startup mode: no props passed → auto-open when appLanguage not yet stored.
  const isStartupMode = openProp === undefined;
  const [startupOpen, setStartupOpen] = useState<boolean>(
    () => isStartupMode && !localStorage.getItem("appLanguage"),
  );

  const open = isStartupMode ? startupOpen : openProp!;

  const saveAndClose = async (code: LangCode) => {
    await i18n.changeLanguage(code);
    localStorage.setItem("appLanguage", code);
    await queryClient.invalidateQueries({
      type: "active",
      predicate: (query) =>
        !NON_LOCALE_QUERY_KEYS.has(query.queryKey[0] as string),
    });
    isStartupMode ? setStartupOpen(false) : onClose?.();
  };

  const handleSelect = async (code: LangCode) => {
    if (!isStartupMode && code === currentLang) { onClose?.(); return; }
    await saveAndClose(code);
  };

  // X button: in startup mode → save English; otherwise just close.
  const handleClose = async () => {
    if (isStartupMode) {
      await saveAndClose("en");
    } else {
      onClose?.();
    }
  };

  if (isStartupMode && !startupOpen) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
            borderRadius: "20px",
            width: { xs: "calc(100% - 32px)", sm: "360px" },
            maxWidth: 400,
            p: 0,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          pt: 2,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="primary">
            {t("language.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("language.subtitle")}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pt: 0, pb: 2 }}>
        <List disablePadding>
          {LANGUAGES.map((lang, index) => {
            const active = currentLang === lang.code;
            return (
              <React.Fragment key={lang.code}>
                <ListItem
                  disablePadding
                  onClick={() => handleSelect(lang.code)}
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: active ? "rgba(1,175,174,0.06)" : "transparent",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" flexDirection="column">
                        <Typography
                          variant="body1"
                          fontWeight={active ? 600 : 400}
                          color={active ? "primary.main" : "text.primary"}
                        >
                          {lang.nativeLabel}
                        </Typography>
                        {lang.nativeLabel !== lang.label && (
                          <Typography variant="caption" color="text.secondary">
                            {lang.label}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {active && (
                    <CheckCircleIcon
                      sx={{ color: "primary.main", fontSize: 22, flexShrink: 0 }}
                    />
                  )}
                </ListItem>
                {index < LANGUAGES.length - 1 && (
                  <Divider sx={{ mx: 1 }} />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageDialog;
